import { FieldConfig } from "components/MergePicker";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ChemicalInput, WeightOrVolume } from "generated-graphql/graphql";

import { HAZARDS_FIELD_NAME } from "./mergeHazards";

export type ReviewFieldSummary = { label: string; value: string };
export type ReviewFieldGroup = { heading?: string; rows: ReviewFieldSummary[][] };

const PURE_OR_MIX_FIELD_NAME = "pureOrMix";

// Row layout, verified against the Chemical edit screen's Grid
// (apps/oak/src/routes/Customer/Chemicals/Catalog/Chemical/index.tsx, the
// container starting ~line 567): [Name | Alternate ID], [CAS Number | EHS
// Form], [State of Matter | Density + Density Units] (Specific Gravity is
// edit-only, computed from density — it isn't part of ChemicalInput, so it
// has no review-step counterpart), then the Basic Information checkboxes
// (Extremely Hazardous, Trade Secret). Pairing is fixed by name rather than
// "next two remaining fields" so an absent field only ever shrinks its own
// row — it can't shift a later pair (e.g. Density/Density Units) apart.
const CORE_ROWS: string[][] = [
  ["chemName", "altID"],
  ["casNumber", "ehsForm"],
  ["stateOfMatter", "density", "densityUnits"],
  ["isEhs", "isTradeSecret"],
  // SDS Upload shares row 4 with the Basic Information block on the edit
  // screen, but that block is 3 stacked fields (isEhs, isTradeSecret,
  // Pure/Mixture) rather than a single field a text input can sit beside —
  // SDS gets its own row here, right after Basic Information, which is the
  // edit screen's position for it.
  ["sds"],
];

// Hazard categories are a full-width section on the edit screen, so a
// one-field row here too. Exclude from Tier II has no edit-screen Grid
// slot at all — it's a header chip + dialog, not a field in this form — so
// it's grouped with hazards, the closest thing it reports on.
const HAZARDS_ROWS: string[][] = [[HAZARDS_FIELD_NAME], ["excludeFromTierII"]];

// Mirrors the edit screen's trailing "Other (less important) Fields" Grid.
const DETAILS_ROWS: string[][] = [
  ["manufacturerSupplier", "recommendedUse"],
  ["version", "revisionDate"],
];

const KNOWN_FIELD_NAMES = new Set([
  ...CORE_ROWS.flat(),
  PURE_OR_MIX_FIELD_NAME,
  ...HAZARDS_ROWS.flat(),
  ...DETAILS_ROWS.flat(),
]);

type ChemicalComponentInput = NonNullable<ChemicalInput["components"]>[number];

function buildComponentSummary(
  component: ChemicalComponentInput,
  index: number
): ReviewFieldSummary {
  const parts: string[] = [];
  if (component.name) parts.push(component.name);
  if (component.componentPercentage != null && component.weightOrVolume) {
    const weightOrVolumeLabel =
      component.weightOrVolume === WeightOrVolume.Weight ? "weight" : "volume";
    parts.push(`${component.componentPercentage}% by ${weightOrVolumeLabel}`);
  }
  if (component.casNumber) parts.push(component.casNumber);
  if (component.isEhs) parts.push("EHS");
  return { label: `Component ${index + 1}`, value: parts.join(" · ") };
}

function isUsableField(
  field: FieldConfig<ChemicalInput> | undefined
): field is FieldConfig<ChemicalInput> {
  return field != null && !field.hidden && field.label != null;
}

function buildSingleFieldSummary(
  field: FieldConfig<ChemicalInput>,
  input: ChemicalInput
): ReviewFieldSummary {
  return { label: field.label ?? "", value: field.render ? field.render(input) : "" };
}

// A row keeps its fields side-by-side unless one of them is multiline, in
// which case each field becomes its own full-width row — a multiline value
// can't share a row the way two short values can.
function splitMultilineRows(fields: ReviewFieldSummary[]): ReviewFieldSummary[][] {
  if (fields.length === 0) return [];
  if (fields.length > 1 && fields.some((field) => field.value.includes("\n"))) {
    return fields.map((field) => [field]);
  }
  return [fields];
}

function chunkRows(summaries: ReviewFieldSummary[], size = 2): ReviewFieldSummary[][] {
  const rows: ReviewFieldSummary[][] = [];
  for (let i = 0; i < summaries.length; i += size) {
    rows.push(...splitMultilineRows(summaries.slice(i, i + size)));
  }
  return rows;
}

/**
 * Builds a section's rows from a fixed name layout: each entry in
 * `rowFieldNames` is the 1-3 field names that share a row (see CORE_ROWS'
 * State of Matter/Density/Density Units row). A row with a missing/empty
 * field renders its surviving fields alone; a row where all are
 * missing/empty is dropped.
 */
function buildRows(
  rowFieldNames: string[][],
  fieldsByName: Map<string, FieldConfig<ChemicalInput>>,
  input: ChemicalInput
): ReviewFieldSummary[][] {
  return rowFieldNames.flatMap((names) => {
    const fields = names
      .map((name) => fieldsByName.get(name))
      .filter(isUsableField)
      .map((field) => buildSingleFieldSummary(field, input))
      .filter((summary) => summary.value !== "");
    return splitMultilineRows(fields);
  });
}

/**
 * The Composition section: the picker table's Pure/Mix `render` packs the
 * mixture kind and every component into one multiline blob (unchanged,
 * still used by that table). The review step instead needs each piece as
 * its own row — the mixture kind alone, then components flowed two per row,
 * mirroring the edit screen's Pure/Mixture radio followed by the Mixture
 * Components table.
 */
function buildCompositionRows(
  fieldsByName: Map<string, FieldConfig<ChemicalInput>>,
  input: ChemicalInput
): ReviewFieldSummary[][] {
  const pureMixField = fieldsByName.get(PURE_OR_MIX_FIELD_NAME);
  if (!isUsableField(pureMixField)) return [];

  const rows: ReviewFieldSummary[][] = [];
  const pureMixValue = prettyPrintEnumValue(input.pureOrMixture);
  if (pureMixValue !== "") rows.push([{ label: pureMixField.label ?? "", value: pureMixValue }]);

  const componentSummaries = (input.components ?? [])
    .map((component, index) => buildComponentSummary(component, index))
    .filter((summary) => summary.value !== "");
  rows.push(...chunkRows(componentSummaries));

  return rows;
}

// Every field the picker table renders that isn't one of the named sections
// above is a dynamic per-jurisdiction state field (see stateFieldFields in
// ChemicalMergePicker), mirroring the edit screen's trailing "State Fields"
// section. Its label reads "State Field (<jurisdiction>):\n<field label>";
// under this form's own "State Fields" heading that prefix is redundant, so
// it's shortened to "<jurisdiction>: <field label>".
function buildStateFieldSummaries(
  mergeFields: FieldConfig<ChemicalInput>[],
  input: ChemicalInput
): ReviewFieldSummary[] {
  return mergeFields
    .filter((field) => !KNOWN_FIELD_NAMES.has(field.name))
    .filter(isUsableField)
    .map((field) => buildSingleFieldSummary(field, input))
    .filter((summary) => summary.value !== "")
    .map((summary) => ({
      ...summary,
      label: summary.label.replace(/^State Field \(([^)]+)\):\s*/u, "$1: "),
    }));
}

/**
 * Row sections for the review step, in the same order and adjacency as the
 * Chemical edit screen: core identity/classification fields, Composition
 * (Pure/Mix + components), Hazards & reporting, Details, then a "State
 * Fields" section for the dynamic per-jurisdiction fields the picker table
 * appends. Sections with no rows (nothing selected, or no state fields on
 * this merge) are omitted.
 */
export function buildReviewFieldSummaries(
  mergeFields: FieldConfig<ChemicalInput>[],
  input: ChemicalInput
): ReviewFieldGroup[] {
  const fieldsByName = new Map(mergeFields.map((field) => [field.name, field]));

  const groups: ReviewFieldGroup[] = [
    { rows: buildRows(CORE_ROWS, fieldsByName, input) },
    { rows: buildCompositionRows(fieldsByName, input) },
    { rows: buildRows(HAZARDS_ROWS, fieldsByName, input) },
    { rows: buildRows(DETAILS_ROWS, fieldsByName, input) },
    { heading: "State Fields", rows: chunkRows(buildStateFieldSummaries(mergeFields, input)) },
  ];

  return groups.filter((group) => group.rows.length > 0);
}
